import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import supabase from '../supabase/supabase';

const { FiMenu, FiX, FiArrowRight, FiLogOut, FiUser, FiLogIn, FiZap, FiLoader } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { lang, setLang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const t = translations[lang].nav;
  const p = translations[lang].paths;

  const mainDomain = "https://stup-on.de/";
  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSession(currentSession);
      
      if (currentSession?.user) {
        try {
          const { data: profile } = await supabase
            .from('profiles_1768156521239')
            .select('is_premium')
            .eq('id', currentSession.user.id)
            .single();
          
          setIsPremium(profile?.is_premium || false);
        } catch (err) {
          console.error("Profile fetch error:", err);
        }
      } else {
        setIsPremium(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
      setSession(currentSession);
      if (currentSession?.user) {
        try {
          const { data: profile } = await supabase
            .from('profiles_1768156521239')
            .select('is_premium')
            .eq('id', currentSession.user.id)
            .single();
          setIsPremium(profile?.is_premium || false);
        } catch (err) {
          console.error("Auth state change profile fetch error:", err);
        }
      } else {
        setIsPremium(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await supabase.auth.signOut();
      setSession(null);
      setIsPremium(false);
      setIsMenuOpen(false);
      navigate(p.home);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleLangToggle = () => {
    const newLang = lang === 'en' ? 'de' : 'en';
    const currentPath = location.pathname;
    const oldPaths = translations[lang].paths;
    const newPaths = translations[newLang].paths;

    let newPath = newPaths.home;
    
    if (currentPath.includes('/steps/') || currentPath.includes('/schritte/')) {
      const id = currentPath.split('/').pop();
      newPath = `${newPaths.steps}/${id}`;
    } else {
      const pathKey = Object.keys(oldPaths).find(key => oldPaths[key] === currentPath);
      if (pathKey && newPaths[pathKey]) {
        newPath = newPaths[pathKey];
      }
    }

    setLang(newLang);
    navigate(newPath);
  };

  const avatarUrl = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=100&h=100";

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <a href={mainDomain} className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiArrowRight} className="text-white text-sm" />
            </div>
            <span className="text-xl font-bold text-gray-900">Erfolgsspur</span>
          </a>

          <nav className="hidden md:flex space-x-8">
            <a 
              href={mainDomain}
              className={`${isActive(p.home) ? 'text-blue-600 font-semibold' : 'text-gray-700'} hover:text-blue-600 transition-colors`}
            >
              {t.home}
            </a>
            <Link 
              to={p.steps} 
              className={`${isActive(p.steps) ? 'text-blue-600 font-semibold' : 'text-gray-700'} hover:text-blue-600 transition-colors`}
            >
              {t.steps}
            </Link>
            <Link 
              to={p.differentiation} 
              className={`${isActive(p.differentiation) ? 'text-blue-600 font-semibold' : 'text-gray-700'} hover:text-blue-600 transition-colors`}
            >
              {t.differentiation}
            </Link>
            <Link 
              to={p.contact} 
              className={`${isActive(p.contact) ? 'text-blue-600 font-semibold' : 'text-gray-700'} hover:text-blue-600 transition-colors`}
            >
              {t.contact}
            </Link>
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <button 
              onClick={handleLangToggle}
              className="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              {lang === 'en' ? 'DE' : 'EN'}
            </button>
            
            {session ? (
              <div className="flex items-center space-x-4">
                {isPremium && (
                  <div className="flex items-center space-x-1 bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest border border-yellow-100">
                    <SafeIcon icon={FiZap} className="text-[10px]" />
                    <span>Premium</span>
                  </div>
                )}
                <button 
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 border border-red-100 rounded-lg transition-all disabled:opacity-50"
                >
                  <SafeIcon icon={isLoggingOut ? FiLoader : FiLogOut} className={isLoggingOut ? 'animate-spin' : ''} />
                  <span>{isLoggingOut ? '...' : t.logout}</span>
                </button>

                <div className="relative group">
                  <div className="w-10 h-10 rounded-full border-2 border-blue-100 p-0.5 overflow-hidden shadow-sm hover:border-blue-500 transition-all cursor-pointer">
                    <img 
                      src={avatarUrl} 
                      alt="User Avatar" 
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all py-2 z-50">
                    <div className="px-4 py-2">
                      <p className="text-xs text-gray-400 truncate font-medium">{session.user.email}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to={p.login} className="px-4 py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors flex items-center space-x-1">
                  <SafeIcon icon={FiLogIn} />
                  <span>{t.login}</span>
                </Link>
                {!isPremium && (
                  <Link to={p.register} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-bold transition-all shadow-md shadow-blue-100">
                    {t.getStarted}
                  </Link>
                )}
              </div>
            )}
          </div>

          <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-gray-700 text-xl" />
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <a href={mainDomain} onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-blue-600 transition-colors">{t.home}</a>
              <Link to={p.steps} onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-blue-600 transition-colors">{t.steps}</Link>
              <Link to={p.differentiation} onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-blue-600 transition-colors">{t.differentiation}</Link>
              <Link to={p.contact} onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-blue-600 transition-colors">{t.contact}</Link>
              <div className="flex flex-col space-y-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <button 
                    onClick={() => {
                      handleLangToggle();
                      setIsMenuOpen(false);
                    }}
                    className="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    {lang === 'en' ? 'DE' : 'EN'}
                  </button>
                  {session ? (
                    <div className="flex items-center space-x-3">
                      <img src={avatarUrl} alt="Avatar" className="w-10 h-10 rounded-full border border-gray-200 object-cover" />
                      <button 
                        onClick={handleLogout} 
                        disabled={isLoggingOut}
                        className="bg-red-50 text-red-600 px-4 py-2 rounded-lg font-bold text-sm border border-red-100 flex items-center space-x-2 disabled:opacity-50"
                      >
                        <SafeIcon icon={isLoggingOut ? FiLoader : FiLogOut} className={isLoggingOut ? 'animate-spin' : ''} />
                        <span>{isLoggingOut ? '...' : t.logout}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Link to={p.login} onClick={() => setIsMenuOpen(false)} className="text-gray-700 font-medium px-3 py-2">{t.login}</Link>
                      {!isPremium && (
                        <Link to={p.register} onClick={() => setIsMenuOpen(false)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-bold transition-colors">
                          {t.getStarted}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;